import { useState } from 'react';
import { attendanceTabBySlug, type AttendanceTab } from '../../constants/attendanceTabs';
import PresenceBoard from './PresenceBoard';
import TimelineTab from './TimelineTab';
import EmployeesTab from './EmployeesTab';
import UnknownFacesTab from './UnknownFacesTab';
import PayrollTab from './PayrollTab';
import AttendanceConfigPage from './AttendanceConfigPage';
import EmployeeDetail from './EmployeeDetail';

export interface AttendancePageProps {
  /** Which surface to render — the `:tab` route segment. */
  slug?: string;
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
  /**
   * Rendered instead of the surface when the host app has no node selected
   * (cloud with an empty node dropdown). Local never passes this.
   */
  emptyState?: React.ReactNode;
}

/**
 * One page per attendance surface — the tab bar became sidebar sub-menu entries,
 * so this shell just resolves the slug and owns the employee drawer (which any
 * surface can open).
 */
export default function AttendancePage({ slug, nodeKey = '', emptyState }: AttendancePageProps) {
  /**
   * The drawer's subject: an employee, and optionally the DAY to open them on. A surface that
   * is itself showing one day (the presence board, when its date filter has been moved off
   * today) passes that day, so the drawer continues the same reading instead of jumping back
   * to today's log. Surfaces with no day of their own pass none and the drawer defaults.
   */
  const [openEmployee, setOpenEmployee] = useState<{ id: string; date?: string } | null>(null);
  const tab: AttendanceTab | undefined = attendanceTabBySlug(slug);

  if (emptyState) return <>{emptyState}</>;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-foreground">{tab?.label || 'Attendance'}</h1>

      {slug === 'presence' && <PresenceBoard nodeKey={nodeKey} onOpenEmployee={(id, date) => setOpenEmployee({ id, date })} />}
      {slug === 'timeline' && <TimelineTab nodeKey={nodeKey} />}
      {slug === 'employees' && <EmployeesTab nodeKey={nodeKey} onOpenEmployee={(id) => setOpenEmployee({ id })} />}
      {slug === 'unknown-faces' && <UnknownFacesTab nodeKey={nodeKey} />}
      {slug === 'payroll' && <PayrollTab nodeKey={nodeKey} onOpenEmployee={(id) => setOpenEmployee({ id })} />}
      {slug === 'config' && <AttendanceConfigPage nodeKey={nodeKey} />}

      {openEmployee && (
        <EmployeeDetail
          employeeId={openEmployee.id}
          initialDate={openEmployee.date}
          onClose={() => setOpenEmployee(null)}
        />
      )}
    </div>
  );
}
